// An animation is anything with update(dt) -> boolean-ish + an isDone() test,
// OR a Promise-returning step. We use a simple interface:

export interface Animation {
  update(dt: number): void;
  isDone(): boolean;
  draw?(ctx: CanvasRenderingContext2D): void;
  onStart?(): void;
  onEnd?(): void;
}

// A track holds animations that run sequentially. Multiple tracks run in parallel.
// This lets us say "play move on track A while damage popup plays on track B".

class Track {
  private queue: Animation[] = [];
  private current: Animation | null = null;

  push(anim: Animation): void {
    this.queue.push(anim);
  }

  update(dt: number): void {
    if (!this.current && this.queue.length > 0) {
      this.current = this.queue.shift()!;
      this.current.onStart?.();
    }
    if (this.current) {
      this.current.update(dt);
      if (this.current.isDone()) {
        this.current.onEnd?.();
        this.current = null;
        // Drain any immediately-done queued anims (e.g., zero-duration).
        if (this.queue.length > 0) this.update(0);
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    this.current?.draw?.(ctx);
  }

  isIdle(): boolean {
    return this.current === null && this.queue.length === 0;
  }

  clear(): void {
    this.queue.length = 0;
    this.current = null;
  }
}

export class AnimationQueue {
  private blockingTrack = new Track();
  private flourishes: Animation[] = [];
  private parallelBuffer: Animation[] | null = null;

  startParallelGroup(): void {
    this.parallelBuffer = [];
  }

  flushParallelGroup(): void {
    if (this.parallelBuffer && this.parallelBuffer.length > 0) {
      this.blockingTrack.push(new ParallelAnimation(this.parallelBuffer));
    }
    this.parallelBuffer = null;
  }

  push(anim: Animation): void {
    if (this.parallelBuffer) {
      this.parallelBuffer.push(anim);
    } else {
      this.blockingTrack.push(anim);
    }
  }

  pushFlourish(anim: Animation): void {
    anim.onStart?.();
    this.flourishes.push(anim);
  }

  update(dt: number): void {
    this.blockingTrack.update(dt);
    for (const a of this.flourishes) if (!a.isDone()) a.update(dt);
    const done = this.flourishes.filter(a => a.isDone());
    for (const a of done) a.onEnd?.();
    this.flourishes = this.flourishes.filter(a => !a.isDone());
  }

  draw(ctx: CanvasRenderingContext2D): void {
    this.blockingTrack.draw(ctx);
    for (const a of this.flourishes) a.draw?.(ctx);
  }

  isIdle(): boolean {
    return this.blockingTrack.isIdle();
  }

  isFullyIdle(): boolean {
    return this.blockingTrack.isIdle() && this.flourishes.length === 0;
  }

  clear(): void {
    this.blockingTrack.clear();
    this.flourishes = [];
  }
}

// Helper: an Animation that is a pure delay. Useful for pacing between turns.
export class DelayAnimation implements Animation {
  private elapsed = 0;
  constructor(private duration: number) {}
  update(dt: number): void {
    this.elapsed += dt;
  }
  isDone(): boolean {
    return this.elapsed >= this.duration;
  }
}

// Helper: an Animation that is a composition of multiple animations running in parallel.
// The composite is done when ALL children are done.
export class ParallelAnimation implements Animation {
  constructor(private children: Animation[]) {}
  onStart(): void {
    for (const c of this.children) c.onStart?.();
  }
  update(dt: number): void {
    for (const c of this.children) if (!c.isDone()) c.update(dt);
  }
  draw(ctx: CanvasRenderingContext2D): void {
    for (const c of this.children) c.draw?.(ctx);
  }
  isDone(): boolean {
    return this.children.every((c) => c.isDone());
  }
  onEnd(): void {
    for (const c of this.children) c.onEnd?.();
  }
}
